import React, { useState, useEffect } from 'react';
import { Plus, Search, Settings, Wrench } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { BusForm } from '../components/buses/BusForm';
import { BusDetailsModal } from '../components/buses/BusDetailsModal';
import { BusTypeModal } from '../components/buses/BusTypeModal';
import { AmenityModal } from '../components/buses/AmenityModal';
import { FeatureModal } from '../components/buses/FeatureModal';
import { BusStats } from '../components/buses/BusStats';
import { BusTable } from '../components/buses/BusTable';
import { BusService } from '../services/busService';
import { Bus, BusType, BusAmenity, BusFeature, BusStats as BusStatsType } from '../types';

export const Buses: React.FC = () => {
  const [buses, setBuses] = useState<Bus[]>([]);
  const [busTypes, setBusTypes] = useState<BusType[]>([]);
  const [amenities, setAmenities] = useState<BusAmenity[]>([]);
  const [features, setFeatures] = useState<BusFeature[]>([]);
  const [stats, setStats] = useState<BusStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showBusForm, setShowBusForm] = useState(false);
  const [showBusDetails, setShowBusDetails] = useState(false);
  const [showBusTypeModal, setShowBusTypeModal] = useState(false);
  const [showAmenityModal, setShowAmenityModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  // Selected items
  const [selectedBus, setSelectedBus] = useState<Bus | null>(null);
  const [editingBus, setEditingBus] = useState<Bus | null>(null);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [nextCursor, setNextCursor] = useState<Date | null>(null);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (cursor?: Date | null) => {
    try {
      setLoading(true);
      const [busesData, busTypesData, amenitiesData, featuresData, statsData] = await Promise.all([
        BusService.getBuses(50, cursor || undefined),
        BusService.getBusTypes(50),
        BusService.getAmenities(100),
        BusService.getFeatures(100),
        BusService.getBusStats()
      ]);

      setBuses(cursor ? (prev => [...prev, ...busesData]) : busesData);
      setBusTypes(busTypesData);
      setAmenities(amenitiesData);
      setFeatures(featuresData);
      setStats(statsData);
      const last = busesData[busesData.length - 1];
      setHasNext(busesData.length === 50);
      setNextCursor(last ? last.createdAt : null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!hasNext || !nextCursor) return;
    try {
      setLoadingMore(true);
      const more = await BusService.getBuses(50, nextCursor);
      setBuses(prev => [...prev, ...more]);
      setHasNext(more.length === 50);
      const last = more[more.length - 1];
      setNextCursor(last ? last.createdAt : nextCursor);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleCreateBus = async (busData: any) => {
    try {
      const newBus = await BusService.createBus(busData);
      setBuses(prev => [newBus, ...prev]);
      setShowBusForm(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateBus = async (busData: any) => {
    try {
      const updatedBus = await BusService.updateBus(editingBus!.id, busData);
      setBuses(prev => prev.map(bus => bus.id === updatedBus.id ? updatedBus : bus));
      setShowBusForm(false);
      setEditingBus(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteBus = async (busId: string) => {
    if (window.confirm('Are you sure you want to delete this bus?')) {
      try {
        await BusService.deleteBus(busId);
        setBuses(prev => prev.filter(bus => bus.id !== busId));
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleEditBus = (bus: Bus) => {
    setEditingBus(bus);
    setShowBusForm(true);
  };

  const handleViewBus = (bus: Bus) => {
    setSelectedBus(bus);
    setShowBusDetails(true);
  };

  const filteredBuses = buses.filter(bus => {
    const matchesSearch = bus.busNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bus.operatorName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || bus.busType?.id === filterType;
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && bus.isActive) ||
      (filterStatus === 'inactive' && !bus.isActive) ||
      (filterStatus === 'in-service' && bus.isInService) ||
      (filterStatus === 'maintenance' && !bus.isInService);

    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Bus Management</h1>
          <p className="text-secondary-600">Manage your fleet of buses, types, amenities, and features</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setShowBusTypeModal(true)}
            className="flex items-center"
          >
            <Settings className="h-4 w-4 mr-2" />
            Bus Types
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowAmenityModal(true)}
            className="flex items-center"
          >
            <Settings className="h-4 w-4 mr-2" />
            Amenities
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFeatureModal(true)}
            className="flex items-center"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Features
          </Button>
          <Button
            onClick={() => setShowBusForm(true)}
            className="flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Bus
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && <BusStats stats={stats} />}

      {/* Search and Filters */}
      <Card className="p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 h-4 w-4" />
              <Input
                placeholder="Search buses by number or operator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Types</option>
              {busTypes?.filter(Boolean).map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="in-service">In Service</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Bus Table */}
      <BusTable
        buses={filteredBuses}
        onEdit={handleEditBus}
        onDelete={handleDeleteBus}
        onView={handleViewBus}
        busTypes={busTypes}
      />

      {/* Modals */}
      {showBusForm && (
        <BusForm
          bus={editingBus}
          busTypes={busTypes}
          amenities={amenities}
          features={features}
          onSubmit={editingBus ? handleUpdateBus : handleCreateBus}
          onClose={() => {
            setShowBusForm(false);
            setEditingBus(null);
          }}
        />
      )}

      {hasNext && (
        <div className="flex justify-center">
          <Button onClick={loadMore} loading={loadingMore} variant="outline">
            Load more
          </Button>
        </div>
      )}

      {showBusDetails && selectedBus && (
        <BusDetailsModal
          bus={selectedBus}
          onClose={() => {
            setShowBusDetails(false);
            setSelectedBus(null);
          }}
        />
      )}

      {showBusTypeModal && (
        <BusTypeModal
          onClose={() => setShowBusTypeModal(false)}
          onRefresh={loadData}
        />
      )}

      {showAmenityModal && (
        <AmenityModal
          onClose={() => setShowAmenityModal(false)}
          onRefresh={loadData}
        />
      )}

      {showFeatureModal && (
        <FeatureModal
          onClose={() => setShowFeatureModal(false)}
          onRefresh={loadData}
        />
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};
